import React, { useState } from 'react'

import { StyleSheet, Keyboard, View, Image, Platform } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'

import { useFormik } from 'formik'
import * as Yup from 'yup'

import client, { authenticationHelper } from '@sart/client'
import { useContent } from '@sart/content'
import { prettyErrorMessage, testID } from '@sart/utilities'
import { adaptive, backgroundColors, typography, colors } from '@sart/styles'

import {
  Title,
  Description,
  Space,
  Anchor,
  Button,
  TextInputExtended,
  KeyboardAvoidingView,
  ReturnFooter
} from '@sart/components'
import { GuruQuestion, CompanyIcon } from '@sart/icons'

import NeedHelpFooter from '../NeedHelpFooter'
import * as theme from './signTheme'

export default function ForgottenPasswordAuth(props) {
  const content = useContent()

  const { navigation, route } = props

  const [resendingCode, setResendingCode] = useState(false)

  const emailAddress = route.params?.emailAddress
  const formik = useFormik({
    initialValues: {
      code: ''
    },
    validationSchema: Yup.object().shape({
      code: Yup.string().trim().required(content.forms.required)
    }),
    onSubmit: handlePhoneVerification
  })

  async function resendCode(values, form) {
    setResendingCode(true)
    try {
      const sms = await client.callApi('outboundCommunication', 'createOutboundCommsForgottenPasswd')
      sms.messageTemplate =
        content.forgotten_password_auth.resendMessageTemplate
      sms.username = emailAddress

      const resendResult = await client.callApi('outboundCommunication', 'modifyOutboundCommsForgottenPasswd', [
        sms.outboundCommsId,
        sms
      ])
    } catch (error) {
      form.touched.code = true
      form.setFieldError('code', prettyErrorMessage(error, 'forgottenPassword', content))
      form.setSubmitting(false)
    }
    setResendingCode(false)
  }

  async function handlePhoneVerification(values, form) {
    Keyboard.dismiss()
    // async call
    try {
      //SB - Commenting out API call to stub for demo - REMOVE THIS
      const authCodeToken = await client.signInWithVerificationCode({
        email: emailAddress,
        code: values.code
      })
      authCodeToken.emailAddress = emailAddress

      // then ...
      navigation.navigate('ForgottenPasswordNewPassword', authCodeToken)
    } catch (error) {
      form.setFieldError('code', prettyErrorMessage(error, 'forgottenPassword'))
      form.setSubmitting(false)
    }
  }

  return (
    <SafeAreaView style={styles.container}>
      <KeyboardAvoidingView style={styles.content}>
        <View style={styles.logo}>
          <Image
              source={{ uri: 'https://shop.bikermobile.com/assets/biker_mobile_logo_final_nobg.png' }}
              style={styles.logoImage}
          />
          {/* <CompanyIcon color={colors.white} height={styles.logoStyle.height} width={200} /> */}
        </View>
        <Space height={24} />

        <Title {...theme.title}>{content.forgotten_password_auth.title}</Title>

        <Space height={24} />

        <Description {...theme.description}>
          {content.forgotten_password_auth.instructions}
        </Description>

        <Space height={24} />

        <TextInputExtended
          label={content.forgotten_password_auth.verificationCodeLabel}
          value={formik.values.code}
          placeholder={
            content.forgotten_password_auth.verificationCodePlaceholder
          }
          onChangeText={formik.handleChange('code')}
          onBlur={formik.handleBlur('code')}
          error={formik.touched.code && formik.errors.code}
          showErrorAndFooter
          {...theme.textInput}
          returnKeyType="go"
          onSubmitEditing={formik.handleSubmit}
          {...testID('forgottenPasswordOTP')}
        />

        <Space height={48} />

        <Button
          title={
            formik.isSubmitting
              ? content.forgotten_password_auth.inProgress
              : content.forgotten_password_auth.action
          }
          disabled={!formik.dirty || !formik.isValid || formik.isSubmitting}
          onPress={formik.handleSubmit}
          {...theme.button}
          titleStyle={{ ...typography.extrabold, fontSize: 18, color: colors.white }}
          disabledTitleStyle={{ ...typography.extrabold, fontSize: 18, color: colors.white + 45 }}
          {...testID('forgottenPasswordOTPButton')}
        />
      </KeyboardAvoidingView>

      {/* <ReturnFooter
        cancelStyle={styles.cancelStyle}
        title={content.common.cancel}
        onPress={() => navigation.navigate('SignIn')}
      /> */}
      <Anchor
        style={styles.footerStyle}
        onPress={() => resendCode(formik.values, formik)}
        title={
          resendingCode
            ? content.forgotten_password_auth.resendLabelActive :
            content.forgotten_password_auth.resendLabel
        }
        {...testID('forgottenPasswordOTPResend')}
      />

    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: backgroundColors.darkCharcoal,
    ...Platform.select({
      web: {
        alignItems: 'center'
      }
    })
  },
  content: {
    flex: 1,
    ...adaptive.wrapperPadding,
    //justifyContent: 'center',
    maxWidth: 600,
    width: '100%'
  },
  cancelStyle: {
    color: colors.white
  },
  footerStyle: {
    color: colors.white,
    textAlign: 'center',
    ...typography.bold,
    fontSize: 16
  },
  logo: {
    alignSelf:"center"
  },
  logoStyle: {
    ...adaptive.guruSize
  },
  logoImage: {
    height: 45,
    width: 45,
    position: 'relative',
    alignSelf: 'center',
    width: 200,
    ...adaptive.guruSize
  },
})
